import * as global from "../../data/global";

/** Stable ids for Site XXXX windows (main-menu + title switcher). */
export const SITE_WINDOW_IDS = {
    comparison: "comparison",
    filter: "filter",
    grades: "grades",
    history: "history",
    cableHealth: "cableHealth",
    inventory: "inventory",
    issues: "issues",
    versioning: "versioning",
    layout: "layout",
    setup: "setup",
    scan: "scan",
    diagnostics: "diagnostics",
    moduleViewer: "moduleViewer",
    sitesScanning: "sitesScanning",
    devices: "devices",
    co2: "co2",
    sites3d: "sites3d",
    bin: "bin",
    remote: "remote",
    ccr: "ccr",
};

/** Temporary instrument opened when CCR is entered without a chosen part. */
export const CCR_DEFAULT_ASSET_TAG = "VEGAPULS6X-75538743";

export const SITE_WINDOW_OPEN_EVENT = "grainwave-open-site-window";

const localeSort = (a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

const GUIDISH = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;
/** Same sentinel as SiteSelectPhase ALL_SITES_OPTION — kept local to avoid import cycles. */
const ALL_SITES_SENTINEL = "__all_sites__";

/** Tools that have an All Sites view — skip the site picker when All Sites is already selected. */
const SITE_WINDOWS_WITH_ALL_SITES = new Set([
    SITE_WINDOW_IDS.versioning,
    SITE_WINDOW_IDS.sitesScanning,
    SITE_WINDOW_IDS.remote,
]);

export function windowSupportsAllSites(windowId) {
    return SITE_WINDOWS_WITH_ALL_SITES.has(windowId);
}

function isDashboardAllSites() {
    const f = global.gw?.siteFilter;
    return f === 0 || f === "0" || f == null || f === "" || f === ALL_SITES_SENTINEL;
}

/**
 * Carry a real site GUID as-is. Carry All Sites only into tools that have that view.
 * With no GUID, use All Sites when the dashboard filter is All and the tool supports it.
 */
export function resolveSiteGuidForWindow(windowId, siteGuid = null) {
    if (siteGuid && siteGuid !== ALL_SITES_SENTINEL) return siteGuid;
    if (siteGuid === ALL_SITES_SENTINEL || (!siteGuid && isDashboardAllSites())) {
        return windowSupportsAllSites(windowId) ? ALL_SITES_SENTINEL : null;
    }
    return null;
}

function looksLikeGuid(value) {
    const s = String(value || "").replace(/[{}]/g, "").trim();
    return GUIDISH.test(s);
}

/**
 * Resolve a site GUID to a display name without flashing the raw GUID while siteInfos loads.
 */
export function resolveSiteDisplayName(siteGuid, siteList = null) {
    if (!siteGuid) return "";
    if (siteGuid === ALL_SITES_SENTINEL) return "All Sites";

    const want = global.normalizeGuidForCompare?.(siteGuid) ?? String(siteGuid).toLowerCase();
    const pools = [];
    if (Array.isArray(siteList) && siteList.length) pools.push(siteList);
    if (Array.isArray(global.gw?.siteInfos) && global.gw.siteInfos.length) {
        pools.push(global.gw.siteInfos);
    }
    const selected = global.gw?.selectedSite;
    if (selected?.siteGuid) pools.push([selected]);

    for (const list of pools) {
        const match = list.find((s) => {
            const g = s?.siteGuid;
            if (!g) return false;
            const have = global.normalizeGuidForCompare?.(g) ?? String(g).toLowerCase();
            return have === want;
        });
        const name =
            match?.siteName
            || match?.main?.siteName
            || match?.data?.main?.siteName
            || match?.data?.siteName;
        if (name) return name;
    }

    // After a refresh, siteInfos is empty and selectedSite may not be restored yet.
    // The last saved site still has a name — use it instead of flashing "Loading…".
    const saved = global.restoreSelectedSite?.();
    if (saved?.siteName) {
        const savedGuid =
            global.normalizeGuidForCompare?.(saved.siteGuid) ?? String(saved.siteGuid || "").toLowerCase();
        if (savedGuid === want) return saved.siteName;
    }

    if (looksLikeGuid(siteGuid)) return "Loading…";
    return String(siteGuid);
}

/**
 * Menu entries for the title Site XXXX switcher / site-card menu.
 * Sorted alphabetically by label. Admin-only items filtered by callers.
 */
export const SITE_WINDOW_MENU_ITEMS = [
    { id: SITE_WINDOW_IDS.bin, label: "Bin 3D", sortLabel: "Bin 3D" },
    { id: SITE_WINDOW_IDS.cableHealth, label: "Cable Health", sortLabel: "Cable Health" },
    { id: SITE_WINDOW_IDS.ccr, label: "CCR", sortLabel: "CCR" },
    { id: SITE_WINDOW_IDS.co2, label: "CO₂", sortLabel: "CO2" },
    { id: SITE_WINDOW_IDS.comparison, label: "Comparison", sortLabel: "Comparison" },
    { id: SITE_WINDOW_IDS.devices, label: "Devices", sortLabel: "Devices" },
    { id: SITE_WINDOW_IDS.diagnostics, label: "Diagnostics", sortLabel: "Diagnostics", adminOnly: true },
    { id: SITE_WINDOW_IDS.grades, label: "Grades", sortLabel: "Grades" },
    { id: SITE_WINDOW_IDS.moduleViewer, label: "Module Viewer", sortLabel: "Module Viewer", adminOnly: true },
    { id: SITE_WINDOW_IDS.history, label: "Sensor History Graph", sortLabel: "Sensor History Graph" },
    { id: SITE_WINDOW_IDS.inventory, label: "Inventory", sortLabel: "Inventory" },
    { id: SITE_WINDOW_IDS.issues, label: "Issues", sortLabel: "Issues", adminOnly: true },
    { id: SITE_WINDOW_IDS.layout, label: "Layout", sortLabel: "Layout", adminOnly: true },
    { id: SITE_WINDOW_IDS.scan, label: "Scan", sortLabel: "Scan" },
    { id: SITE_WINDOW_IDS.remote, label: "Remote", sortLabel: "Remote", adminOnly: true },
    { id: SITE_WINDOW_IDS.sitesScanning, label: "Sites Scanning", sortLabel: "Sites Scanning", adminOnly: true },
    { id: SITE_WINDOW_IDS.setup, label: "Site Setup", sortLabel: "Site Setup", adminOnly: true },
    { id: SITE_WINDOW_IDS.sites3d, label: "Site 3D", sortLabel: "Site 3D" },
    { id: SITE_WINDOW_IDS.versioning, label: "Versioning", sortLabel: "Versioning", adminOnly: true },
];

/** Main-menu-only site tools (not shown in the title Site XXXX switcher). */
const MAIN_MENU_ONLY_SITE_ITEMS = [
    { id: SITE_WINDOW_IDS.filter, label: "Filter", sortLabel: "Filter" },
];

function isAdminViewer() {
    return Boolean(global.isAdminUser?.(global.gw?.email));
}

function sortMenuItems(items) {
    return items
        .slice()
        .sort((a, b) => localeSort(a.sortLabel || a.label, b.sortLabel || b.label));
}

/** Tool menu (Site XXXX title switcher) — alphabetical, admin-filtered. */
export function getSiteWindowMenuItems() {
    const isAdmin = isAdminViewer();
    return sortMenuItems(
        SITE_WINDOW_MENU_ITEMS.filter((item) => !item.adminOnly || isAdmin),
    );
}

/**
 * Site-card hamburger menu — same tools as the title switcher (alphabetical).
 */
export function getSiteCardMenuItems() {
    return getSiteWindowMenuItems();
}

/**
 * Main app menu site section — no "Site " prefix (except names that already include it,
 * e.g. Site Setup / Site 3D), alphabetical.
 */
export function getMainMenuSiteItems() {
    const isAdmin = isAdminViewer();
    return sortMenuItems(
        [...MAIN_MENU_ONLY_SITE_ITEMS, ...SITE_WINDOW_MENU_ITEMS].filter(
            (item) => !item.adminOnly || isAdmin,
        ),
    );
}

/**
 * Ask ProtectedRoute (or any listener) to open another Site XXXX window,
 * optionally carrying the currently loaded site GUID so the target can skip its picker.
 */
export function openSiteWindow(windowId, siteGuid = null) {
    if (typeof window === "undefined" || !window.dispatchEvent) return;
    // Only a guid the caller passed counts. The main menu passes none, and
    // selectedSite can still hold the last site card that loaded (often the
    // last row on the sites list). Falling back to it skipped the picker.
    window.dispatchEvent(
        new CustomEvent(SITE_WINDOW_OPEN_EVENT, {
            detail: {
                windowId,
                siteGuid: resolveSiteGuidForWindow(windowId, siteGuid) || null,
            },
        }),
    );
}
